import TencentCloudChat from '@tencentcloud/chat';

const DB_NAME = 'spt-im-cache';
const DB_VERSION = 2;
const STORE_IM_CACHE = 'im-cache';
const CACHE_SWITCH_KEY = 'imCacheEnabled';
const CACHE_DEBUG_KEY = 'imCacheDebug';
const MSG_EXT_DATA_KEY = '__spt_ext_data__';

const MESSAGE_PAGE_SIZE = 20;
const MESSAGE_CACHE_LIMIT = 1000;
const DEFAULT_DEBOUNCE_MS = 500;

let dbPromise = null;
const memoryRecords = new Map();
const flushTimerMap = new Map();

const deleteDb = async () => {
  if (!isIndexedDbAvailable()) {
    return false;
  }
  return new Promise((resolve) => {
    const req = window.indexedDB.deleteDatabase(DB_NAME);
    req.onsuccess = () => resolve(true);
    req.onerror = () => resolve(false);
    req.onblocked = () => resolve(false);
  });
};

const isIndexedDbAvailable = () => typeof window !== 'undefined' && !!window.indexedDB;

const isDebugOn = () => {
  if (typeof window === 'undefined' || !window.localStorage) {
    return false;
  }
  return window.localStorage.getItem(CACHE_DEBUG_KEY) === 'true';
};

const debugLog = (...args) => {
  if (isDebugOn()) {
    console.log('[IM_CACHE_DEBUG]', ...args);
  }
};

const getCacheSwitch = () => {
  if (typeof window === 'undefined' || !window.localStorage) {
    return true;
  }
  return window.localStorage.getItem(CACHE_SWITCH_KEY) !== 'false';
};

const safeClone = (value) => {
  try {
    return JSON.parse(JSON.stringify(value));
  } catch (error) {
    debugLog('safeClone failed', error);
    return null;
  }
};

const openDb = () => {
  if (!isIndexedDbAvailable()) {
    return Promise.resolve(null);
  }
  if (dbPromise) {
    return dbPromise;
  }
  dbPromise = new Promise((resolve) => {
    const request = window.indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      if (db.objectStoreNames.contains('conversations') && !db.objectStoreNames.contains(STORE_IM_CACHE)) {
        const oldStore = event.target.transaction.objectStore('conversations');
        const newStore = db.createObjectStore(STORE_IM_CACHE, { keyPath: 'key' });
        oldStore.openCursor().onsuccess = (cursorEvent) => {
          const cursor = cursorEvent.target.result;
          if (cursor) {
            newStore.put(cursor.value);
            cursor.continue();
          }
        };
      } else if (!db.objectStoreNames.contains(STORE_IM_CACHE)) {
        db.createObjectStore(STORE_IM_CACHE, { keyPath: 'key' });
      }
      if (db.objectStoreNames.contains('conversations')) {
        db.deleteObjectStore('conversations');
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => {
      debugLog('openDb failed', request.error);
      resolve(null);
    };
  });
  return dbPromise;
};

const recoverDb = async (reason) => {
  debugLog('recoverDb start', reason);
  try {
    const db = await dbPromise;
    if (db && typeof db.close === 'function') {
      db.close();
    }
  } catch (error) {
    debugLog('recoverDb close old db failed', error);
  }
  dbPromise = null;
  await deleteDb();
  const db = await openDb();
  debugLog('recoverDb done', !!db);
  return db;
};

const withStore = async (mode, callback, retried = false) => {
  const db = await openDb();
  if (!db) {
    return null;
  }
  let tx = null;
  try {
    tx = db.transaction([STORE_IM_CACHE], mode);
  } catch (error) {
    debugLog('withStore failed', error);
    if (!retried && error?.name === 'NotFoundError') {
      await recoverDb('store missing');
      return withStore(mode, callback, true);
    }
    return null;
  }
  return new Promise((resolve) => {
    try {
      const store = tx.objectStore(STORE_IM_CACHE);
      callback(store, resolve);
    } catch (error) {
      debugLog('withStore failed after tx', error);
      resolve(null);
    }
  });
};

const readRecordFromDb = async (key) => {
  return withStore('readonly', (store, resolve) => {
    const req = store.get(key);
    req.onsuccess = () => resolve(req.result || null);
    req.onerror = () => resolve(null);
  });
};

const writeRecordToDb = async (record) => {
  return withStore('readwrite', (store, resolve) => {
    const req = store.put(record);
    req.onsuccess = () => resolve(true);
    req.onerror = () => resolve(false);
  });
};

const deleteRecordFromDb = async (key) => {
  return withStore('readwrite', (store, resolve) => {
    const req = store.delete(key);
    req.onsuccess = () => resolve(true);
    req.onerror = () => resolve(false);
  });
};

const scanAndPatchRecords = async (prefix, patcher) => {
  return withStore('readwrite', (store, resolve) => {
    const req = store.openCursor();
    req.onsuccess = () => {
      const cursor = req.result;
      if (!cursor) {
        resolve(true);
        return;
      }
      const value = cursor.value;
      if (value?.key && value.key.startsWith(prefix)) {
        const patched = patcher(safeClone(value));
        if (patched) {
          cursor.update(patched);
          memoryRecords.set(value.key, patched);
        }
      }
      cursor.continue();
    };
    req.onerror = () => resolve(false);
  });
};

const getRecord = async (key) => {
  if (!key || !getCacheSwitch()) {
    return null;
  }
  if (memoryRecords.has(key)) {
    return safeClone(memoryRecords.get(key));
  }
  const fromDb = await readRecordFromDb(key);
  if (fromDb) {
    memoryRecords.set(key, fromDb);
    return safeClone(fromDb);
  }
  return null;
};

const flushRecord = async (key) => {
  if (!memoryRecords.has(key) || !getCacheSwitch()) {
    return false;
  }
  const record = safeClone(memoryRecords.get(key));
  if (!record) {
    return false;
  }
  const ok = await writeRecordToDb(record);
  if (!ok) {
    debugLog('flushRecord failed', key);
  }
  return ok;
};

const setRecord = async (key, data, debounceMs = DEFAULT_DEBOUNCE_MS) => {
  if (!key || !getCacheSwitch()) {
    return false;
  }
  const record = {
    key,
    data: safeClone(data),
    updatedAt: Date.now(),
  };
  if (record.data == null) {
    return false;
  }
  memoryRecords.set(key, record);
  if (!debounceMs || debounceMs <= 0) {
    return flushRecord(key);
  }
  if (flushTimerMap.has(key)) {
    clearTimeout(flushTimerMap.get(key));
  }
  const timer = setTimeout(() => {
    flushTimerMap.delete(key);
    flushRecord(key).then(() => { }).catch(() => { });
  }, debounceMs);
  flushTimerMap.set(key, timer);
  return true;
};

const deleteRecord = async (key) => {
  if (!key) {
    return false;
  }
  if (flushTimerMap.has(key)) {
    clearTimeout(flushTimerMap.get(key));
    flushTimerMap.delete(key);
  }
  memoryRecords.delete(key);
  return deleteRecordFromDb(key);
};

const toConversationKey = (userId) => `${userId}:conversations`;
const toMessageKey = (userId, conversationID) => `${userId}:conversation:${conversationID}`;

const getMessageId = (message) => message?.ID || message?.id || message?.msgID || '';
const getMessageTime = (message) => Number(message?.time || message?.lastTime || message?.clientTime || 0);
const getMessageSeq = (message) => Number(message?.sequence || message?.lastSequence || message?.seq || message?.messageSeq || 0);

const dedupeMessages = (messages = []) => {
  // 相通ID的对象，只保留数组中第一个
  const seen = new Set();
  const result = [];
  messages.forEach((message) => {
    const id = getMessageId(message);
    if (!id || seen.has(id)) {
      return;
    }
    seen.add(id);
    result.push(message);
  });
  return result;
};

const sortMessagesAsc = (messages = []) => {
  return [...messages].sort((a, b) => {
    const ta = getMessageTime(a);
    const tb = getMessageTime(b);
    if (ta !== tb) {
      return ta - tb;
    }
    const sa = getMessageSeq(a);
    const sb = getMessageSeq(b);
    if (sa !== sb) {
      return sa - sb;
    }
    return `${getMessageId(a)}`.localeCompare(`${getMessageId(b)}`);
  });
};

const appendMessageWithRules = (targetMessages, message, isGroup) => {
  const id = getMessageId(message);
  if (!id) {
    return false;
  }
  if (targetMessages.find((item) => getMessageId(item) === id)) {
    return false;
  }
  if (!targetMessages.length) {
    targetMessages.push(message);
    return true;
  }
  const last = targetMessages[targetMessages.length - 1];
  const canAppend = isGroup
    ? getMessageSeq(message) > getMessageSeq(last)
    : getMessageTime(message) > getMessageTime(last);
  if (!canAppend) {
    return false;
  }
  targetMessages.push(message);
  return true;
};

const updateExistMessage = (targetMessages, message) => {
  const id = getMessageId(message);
  if (!id) {
    return false;
  }
  const index = targetMessages.findIndex((item) => getMessageId(item) === id);
  if (index < 0) {
    return false;
  }
  // #warning 未确定扩展属性名称。确定后，这里要对扩展属性做判断再合并
  assignMessage(targetMessages[index], message);
  return true;
}

const assignMessage = (targetMessage, sourceMessage) => {
  return Object.assign(targetMessage, sourceMessage);
}

const mergeMessagesWithAssign = (baseMessages = [], incomingMessages = []) => {
  const merged = [];
  const upsert = (message) => {
    const id = getMessageId(message);
    if (!id) {
      return;
    }
    const index = merged.findIndex((item) => getMessageId(item) === id);
    if (index < 0) {
      merged.push(message);
      return;
    }
    assignMessage(merged[index], message);
  };

  baseMessages.forEach(upsert);
  incomingMessages.forEach(upsert);
  return sortMessagesAsc(merged);
}

const trimForLimit = (messages = [], limit = MESSAGE_CACHE_LIMIT) => {
  if (messages.length <= limit) {
    return {
      messages,
      trimmedCount: 0,
      // nextReqMessageID: null,
    };
  }
  const trimmedMessages = messages.slice(messages.length - limit);
  return {
    messages: trimmedMessages,
    trimmedCount: messages.length - trimmedMessages.length,
    // nextReqMessageID: trimmedMessages.length ? getMessageId(trimmedMessages[0]) : null,
  };
};

const getDefaultMessageState = () => ({
  coreMessages: [],
  coreBoundaryNextReqMessageID: null,
  coreBoundaryCompleted: false,
  detachedMessages: [],
  detachedBoundaryNextReqMessageID: null,
  detachedBoundaryCompleted: false,
});

const readMessageState = async (userId, conversationID) => {
  const record = await getRecord(toMessageKey(userId, conversationID));
  if (!record || !record.data || typeof record.data !== 'object') {
    return getDefaultMessageState();
  }
  const state = record.data;
  return {
    coreMessages: Array.isArray(state.coreMessages) ? mergeMessagesWithAssign([], state.coreMessages) : [],
    coreBoundaryNextReqMessageID: state.coreBoundaryNextReqMessageID || null,
    coreBoundaryCompleted: !!state.coreBoundaryCompleted,
    detachedMessages: Array.isArray(state.detachedMessages) ? mergeMessagesWithAssign([], state.detachedMessages) : [],
    detachedBoundaryNextReqMessageID: state.detachedBoundaryNextReqMessageID || null,
    detachedBoundaryCompleted: !!state.detachedBoundaryCompleted,
  };
};

const writeMessageState = async (userId, conversationID, state, debounceMs = DEFAULT_DEBOUNCE_MS) => {
  const nextState = {
    coreMessages: trimForLimit(mergeMessagesWithAssign([], state.coreMessages || [])).messages,
    coreBoundaryNextReqMessageID: state.coreBoundaryNextReqMessageID || null,
    coreBoundaryCompleted: !!state.coreBoundaryCompleted,
    detachedMessages: trimForLimit(mergeMessagesWithAssign([], state.detachedMessages || [])).messages,
    detachedBoundaryNextReqMessageID: state.detachedBoundaryNextReqMessageID || null,
    detachedBoundaryCompleted: !!state.detachedBoundaryCompleted,
  };
  return setRecord(toMessageKey(userId, conversationID), nextState, debounceMs);
};

const isVisibleMsg = (e, role, userId) => {
  if (e.type == TencentCloudChat.TYPES.MSG_GRP_TIP && e.payload.operationType == 7) {
    return false;//过滤掉群提示信息的类型7
  } else if (e.type == TencentCloudChat.TYPES.MSG_GRP_TIP && [2, 3].some(s => s == e.payload.operationType) && !["Owner", "Admin"].some(e => e == role)) {//不是管理员不显示退群和踢人信息
    return false;
  } else if (e.type == TencentCloudChat.TYPES.MSG_CUSTOM) {
    let _e = JSON.parse(JSON.stringify(e));
    if (typeof _e.payload.data == 'string') {
      try {
        _e.payload.data = JSON.parse(_e.payload.data);
      } catch (err) { }
    }
    if (_e.payload.data?.event == 'redPacketReceive' && _e.payload.data?.data.senderId != userId) {//不是自己的红包回执不要
      return false;
    } else if (_e.payload.data?.event == "memberRemoved" && !["Owner", "Admin"].some(e => e == role)) {//不是管理员不显示退群和踢人信息
      return false;
    } else {
      return true;
    }
  } else {
    return true;
  }
};

// 返回的消息列表不包含 cursorId对应的消息，且是cursorId之前的pageSize条消息
const getPageBeforeCursor = (messages = [], cursorId, pageSize = MESSAGE_PAGE_SIZE, role, userId) => {
  var index = messages.length;
  if (cursorId) {
    index = messages.findIndex((item) => getMessageId(item) === cursorId);
  }
  if (index <= 0) {
    return [];
  }
  const page = [];
  for (let i = index - 1; i >= 0 && page.length < pageSize; i--) {
    if (isVisibleMsg(messages[i], role, userId)) {
      page.unshift(messages[i]);
    } else {
      //不需要显示的信息，比如系统消息，或者撤回消息等，直接过滤掉，不计入pageSize限制，但仍然继续往前找，直到找到pageSize条需要显示的消息，或者没有消息了为止
    }
  }
  return page;
};

const composeMessageListResponse = ({
  list,
  state,
  targetKey
}) => {
  let nextReqMessageID = '';
  let isCompleted = false;
  if (!list.length) {
    nextReqMessageID = '';
    isCompleted = true;
  } else {
    const first = list[0];
    const sourceMessages = state[targetKey] || [];
    const firstIndex = sourceMessages.findIndex((item) => getMessageId(item) === getMessageId(first));
    if (firstIndex <= 0) {
      nextReqMessageID = targetKey === 'coreMessages' ? state.coreBoundaryNextReqMessageID : state.detachedBoundaryNextReqMessageID;
      isCompleted = targetKey === 'coreMessages' ? !!state.coreBoundaryCompleted : !!state.detachedBoundaryCompleted;
    } else {
      // firstIndex must be > 0 here, so firstIndex - 1 is safe
      nextReqMessageID = getMessageId(sourceMessages[firstIndex]) || '';
      isCompleted = false;
    }
  }
  return {
    data: {
      messageList: list,
      nextReqMessageID,
      isCompleted,
    },
  };
};

const resolveConversationLastMessageFingerprint = async (userId, conversationID) => {
  const record = await getRecord(toConversationKey(userId));
  const list = record?.data;
  if (!Array.isArray(list)) {
    return '';
  }
  const matched = list.find((item) => item?.conversationID === conversationID);
  if (!matched) {
    return {
      type: /^GROUP/.test(conversationID) ? TencentCloudChat.TYPES.CONV_GROUP : TencentCloudChat.TYPES.CONV_C2C,
    }
  } else {
    return {
      type: matched?.type,
      lastSeq: matched?.lastMessage ? getMessageSeq(matched.lastMessage) : 0,
      lastTime: matched?.lastMessage ? getMessageTime(matched.lastMessage) : 0,
    };
  }
};

const isGroupConversation = (conversationID, message) => {
  if (message?.conversationType) {
    return message.conversationType === TencentCloudChat.TYPES.CONV_GROUP;
  }
  return `${conversationID}`.startsWith('GROUP');
};

export const getUserConversationsCache = async (userId, maxAgeMs = 45000, requireFresh = false) => {
  if (!userId || !getCacheSwitch()) {
    return null;
  }
  const record = await getRecord(toConversationKey(userId));
  if (!record || !Array.isArray(record.data)) {
    return null;
  }
  if (requireFresh && Date.now() - Number(record.updatedAt || 0) > maxAgeMs) {
    return null;
  }
  return record.data;
};

export const createImCacheManager = ({
  getChatClient,
  getUserId,
}) => {
  const getCurrentUserId = () => `${getUserId?.() || ''}`;
  const createStats = () => ({
    conversationList: { total: 0, hit: 0, miss: 0, bypass: 0, remote: 0 },
    messageList: { total: 0, hit: 0, miss: 0, bypass: 0, remote: 0, boundaryHit: 0 },
    writes: { conversationPushUpdate: 0, messagePushUpdate: 0, detachedClear: 0 },
  });
  let stats = createStats();
  let verboseTrace = false;
  const pendingConversationMap = new Map();

  const isTraceOn = () => {
    if (verboseTrace || isDebugOn()) {
      return true;
    }
    if (typeof window === 'undefined' || !window.localStorage) {
      return false;
    }
    return window.localStorage.getItem('imCacheTrace') === 'true';
  };

  const trace = (...args) => {
    if (isTraceOn()) {
      console.log('[IM_CACHE_TRACE]', ...args);
    }
  };

  const inc = (scope, key) => {
    if (!stats[scope]) {
      return;
    }
    stats[scope][key] = Number(stats[scope][key] || 0) + 1;
  };

  const getConversationList = async () => {
    inc('conversationList', 'total');
    const userId = getCurrentUserId();
    const chatClient = await getChatClient();
    if (!userId || !getCacheSwitch()) {
      inc('conversationList', 'bypass');
      inc('conversationList', 'remote');
      trace('MISS conversationList -> SDK.getConversationList (reason: bypass cache)', { userId, cacheEnabled: getCacheSwitch() });
      return chatClient.getConversationList();
    }
    const cached = await getRecord(toConversationKey(userId));
    if (Array.isArray(cached?.data)) {
      inc('conversationList', 'hit');
      trace('HIT conversationList', { userId, count: cached.data.length });
      return { data: { conversationList: cached.data } };
    }
    if (pendingConversationMap.has(userId)) {
      inc('conversationList', 'miss');
      trace('MISS conversationList -> reuse pending SDK.getConversationList', { userId });
      return pendingConversationMap.get(userId);
    }
    inc('conversationList', 'miss');
    inc('conversationList', 'remote');
    trace('MISS conversationList -> SDK.getConversationList (reason: cache miss)', { userId });
    const pending = (async () => {
      const remote = await chatClient.getConversationList();
      const remoteList = remote?.data?.conversationList || [];
      if (Array.isArray(remoteList)) {
        await setRecord(toConversationKey(userId), remoteList, DEFAULT_DEBOUNCE_MS);
      }
      return remote;
    })();
    pendingConversationMap.set(userId, pending);
    try {
      return await pending;
    } finally {
      pendingConversationMap.delete(userId);
    }
  };

  const handleConversationListUpdate = async (conversationList = []) => {
    const userId = getCurrentUserId();
    if (!userId || !Array.isArray(conversationList)) {
      return;
    }
    inc('writes', 'conversationPushUpdate');
    await setRecord(toConversationKey(userId), conversationList, DEFAULT_DEBOUNCE_MS);
  };

  const onLoginSuccess = async () => {
    // 登录时不需要清理游离缓存。获取消息时再判断和执行
    // const userId = getCurrentUserId();
    // if (!userId) {
    //   return;
    // }
    // const prefix = `${userId}:conversation:`;
    // await scanAndPatchRecords(prefix, (record) => {
    //   if (!record?.data || typeof record.data !== 'object') {
    //     return null;
    //   }
    //   record.data.detachedMessages = [];
    //   record.updatedAt = Date.now();
    //   return record;
    // });
    // inc('writes', 'loginDetachedClear');
  };

  // extMap 格式：{ [messageId]: { a: 1, b: 2 } }，会和原有扩展对象合并
  // 设置缓存中的消息的扩展信息，并同步落库
  const writeMsgsExt = async (conversationID, extMap = {}) => {
    const userId = getCurrentUserId();
    if (!userId || !conversationID || !extMap || typeof extMap !== 'object' || Array.isArray(extMap) || !Object.keys(extMap).length || !getCacheSwitch()) {
      return { updatedIds: [], missedIds: [] };
    }
    const patchMap = new Map();
    Object.keys(extMap).forEach((rawId) => {
      const id = `${rawId || ''}`;
      if (!id) {
        return;
      }
      const payload = extMap[id];
      if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
        return;
      }
      const extPayload = safeClone(payload);
      if (!extPayload || typeof extPayload !== 'object') {
        return;
      }
      patchMap.set(id, extPayload);
    });
    if (!patchMap.size) {
      return { updatedIds: [], missedIds: [] };
    }

    const state = await readMessageState(userId, conversationID);
    const updatedIds = new Set();
    const applyPatch = (targetMessages = []) => {
      targetMessages.forEach((message) => {
        const id = getMessageId(message);
        if (!id || !patchMap.has(id)) {
          return;
        }
        const patch = patchMap.get(id) || {};
        const prevExt = message && typeof message === 'object' ? message[MSG_EXT_DATA_KEY] : null;
        const mergedExt = Object.assign({}, (prevExt && typeof prevExt === 'object') ? prevExt : {}, patch);
        assignMessage(message, { [MSG_EXT_DATA_KEY]: mergedExt });
        updatedIds.add(id);
      });
    };

    applyPatch(state.coreMessages);
    applyPatch(state.detachedMessages);

    if (updatedIds.size) {
      await writeMessageState(userId, conversationID, state, DEFAULT_DEBOUNCE_MS);
    }

    const missedIds = Array.from(patchMap.keys()).filter((id) => !updatedIds.has(id));
    return {
      updatedIds: Array.from(updatedIds),
      missedIds,
    };
  };

  // 读取缓存中消息的扩展信息的副本
  const readMsgsExt = async (conversationID, messageIDs = []) => {
    const userId = getCurrentUserId();
    if (!userId || !conversationID || !getCacheSwitch()) {
      return {};
    }
    if (!Array.isArray(messageIDs) || !messageIDs.length) {
      return {};
    }

    const wanted = new Set(messageIDs.map((id) => `${id || ''}`).filter(Boolean));
    if (!wanted.size) {
      return {};
    }

    const extMap = {};
    const state = await readMessageState(userId, conversationID);
    const allMessages = (state.coreMessages || []).concat(state.detachedMessages || []);
    allMessages.forEach((message) => {
      const id = getMessageId(message);
      if (!id || !wanted.has(id)) {
        return;
      }
      const extData = message && typeof message === 'object' ? message[MSG_EXT_DATA_KEY] : null;
      if (extData && typeof extData === 'object') {
        extMap[id] = safeClone(extData);
      }
    });
    return extMap;
  };

  // 获取 message 的扩展数据。返回值是一个对象，可能包含任意字段，取决于之前写入的内容
  // 特性1：如果原message没有扩展数据，则返回null
  // 特性2：返回消息对象的“引用”——外部修改可引发message的变动！
  const getMsgExt = (message) => {
    if (!message || typeof message !== 'object') {
      return null;
    }
    const extData = message[MSG_EXT_DATA_KEY];
    if (extData && typeof extData === 'object') {
      return extData;
    }
    return null;
  };

  // 设置 message 的扩展数据。ext 是一个对象，可能包含任意字段，取决于业务需求
  // 特性1：如果 message 参数无效，或者 ext 参数无效，则不执行任何操作，返回 null
  // 特性2：如果 message 原来没有扩展数据，则创建一个新的扩展对象并与 message 关联；
  // 特性3：返回 message 的扩展对象的“引用”——外部修改可引发message的变动！
  const setMsgExt = (message, ext = {}) => {
    if (!message || typeof message !== 'object' || !ext || typeof ext !== 'object' || Array.isArray(ext)) {
      return null;
    }
    const prevExt = message[MSG_EXT_DATA_KEY];
    if (prevExt && typeof prevExt === 'object' && !Array.isArray(prevExt)) {
      Object.assign(prevExt, ext);
      return prevExt;
    }
    // const nextExt = Object.assign({}, ext);
    assignMessage(message, { [MSG_EXT_DATA_KEY]: ext });
    return message[MSG_EXT_DATA_KEY];
  };

  const handleMessageReceived = async (messages = []) => {
    const userId = getCurrentUserId();
    if (!userId || !Array.isArray(messages) || !messages.length || !getCacheSwitch()) {
      return;
    }
    const grouped = new Map();
    messages.forEach((message) => {
      const conversationID = message?.conversationID;
      if (!conversationID) {
        return;
      }
      if (!grouped.has(conversationID)) {
        grouped.set(conversationID, []);
      }
      grouped.get(conversationID).push(message);

    });

    for (const [conversationID, list] of grouped.entries()) {
      const state = await readMessageState(userId, conversationID);
      for (const message of list) {
        //依赖ID，先更新一次。这样可以确保time和seq对上
        updateExistMessage(state.coreMessages, message);
        updateExistMessage(state.detachedMessages, message);
      }

      let isContinuous = false;
      const conversationLastMessageFingerprint = await resolveConversationLastMessageFingerprint(userId, conversationID);
      if (!state.coreMessages.length) isContinuous = false;
      else {
        if (conversationLastMessageFingerprint.type == TencentCloudChat.TYPES.CONV_GROUP) {
          isContinuous = getMessageSeq(state.coreMessages[state.coreMessages.length - 1]) >= conversationLastMessageFingerprint.lastSeq;
        } else {
          isContinuous = getMessageTime(state.coreMessages[state.coreMessages.length - 1]) >= conversationLastMessageFingerprint.lastTime;
        }
      }

      if (!isContinuous && state.detachedMessages.length) {
        // 确保不会产生二次游离数据集
        let isDetachedContinuous = false;
        if (conversationLastMessageFingerprint.type == TencentCloudChat.TYPES.CONV_GROUP) {
          isDetachedContinuous = getMessageSeq(state.detachedMessages[state.detachedMessages.length - 1]) >= conversationLastMessageFingerprint.lastSeq;
        } else {
          isDetachedContinuous = getMessageTime(state.detachedMessages[state.detachedMessages.length - 1]) >= conversationLastMessageFingerprint.lastTime;
        }
        if (!isDetachedContinuous) {
          state.detachedMessages = [];
          state.detachedBoundaryNextReqMessageID = null;
          trace('游离缓存不连续，已清空', { conversationID, conversationLastMessageFingerprint, detachedLastMessage: state.detachedMessages[state.detachedMessages.length - 1] });
          inc('writes', 'detachedClear');
        }
      }

      const sortedIncoming = mergeMessagesWithAssign([], list);
      sortedIncoming.forEach((message) => {
        const isGroup = isGroupConversation(conversationID, message);
        if (isContinuous) {
          const appended = appendMessageWithRules(state.coreMessages, message, isGroup);
          if (appended && state.detachedMessages.length) {
            state.detachedMessages = [];
          }
        } else {
          appendMessageWithRules(state.detachedMessages, message, isGroup);
        }
      });

      if (state.coreMessages.length > MESSAGE_CACHE_LIMIT) {
        let trimmed = trimForLimit(state.coreMessages, MESSAGE_CACHE_LIMIT);
        state.coreMessages = trimmed.messages;
        if (trimmed.trimmedCount > 0) {
          state.coreBoundaryCompleted = false;
          state.coreBoundaryNextReqMessageID = trimmed.nextReqMessageID || state.coreBoundaryNextReqMessageID;
        }
      }
      if (state.detachedMessages.length > MESSAGE_CACHE_LIMIT) {
        let trimmed = trimForLimit(state.detachedMessages, MESSAGE_CACHE_LIMIT);
        state.detachedMessages = trimmed.messages;
        if (trimmed.trimmedCount > 0) {
          state.detachedBoundaryCompleted = false;
          state.detachedBoundaryNextReqMessageID = trimmed.nextReqMessageID || state.detachedBoundaryNextReqMessageID;
        }
      }

      inc('writes', 'messagePushUpdate');
      console.log('handleMessageReceived', state);
      await writeMessageState(userId, conversationID, state, DEFAULT_DEBOUNCE_MS);
    }
  };
  const handleMessageReadReceipt = async (messages) => {
    const userId = getCurrentUserId();
    if (!userId || !Array.isArray(messages) || !messages.length || !getCacheSwitch()) {
      return;
    }
    const grouped = new Map();
    messages.forEach((message) => {
      const conversationID = message?.conversationID;
      if (!conversationID) {
        return;
      }
      if (!grouped.has(conversationID)) {
        grouped.set(conversationID, []);
      }
      grouped.get(conversationID).push(message);
    });
    for (const [conversationID, list] of grouped.entries()) {
      const state = await readMessageState(userId, conversationID);
      list.forEach(message => {
        [state.detachedMessages, state.coreMessages].forEach(data => {
          data.forEach(e => {
            if (e.ID == message.messageID) {
              e.isPeerRead = true;
              e.readReceiptInfo.isPeerRead = true;
            }
          })
        })
      })
      trace('handleMessageReadReceipt');
      await writeMessageState(userId, conversationID, state, DEFAULT_DEBOUNCE_MS);
    }
  }
  // 标记消息的撤回
  const userRevokeMessage = async (users, conversationID) => {
    const userId = getCurrentUserId();
    if (!userId || !Array.isArray(users) || !users.length || !getCacheSwitch()) {
      return;
    }
    const grouped = new Map();
    users.forEach((user) => {
      if (!conversationID) {
        return;
      }
      if (!grouped.has(conversationID)) {
        grouped.set(conversationID, []);
      }
      grouped.get(conversationID).push(user);
    });
    for (const [conversationID, list] of grouped.entries()) {
      const state = await readMessageState(userId, conversationID);
      list.forEach(userID => {
        [state.detachedMessages, state.coreMessages].forEach(data => {
          data.filter(e => !e.isRevoked).forEach(e => {
            if (e.from == userID) {
              e.isRevoked = true;
            }
          });
        })
      })
      trace('userRevokeMessage');
      await writeMessageState(userId, conversationID, state, DEFAULT_DEBOUNCE_MS);
    }
  }
  const deleteMessage = async (messages) => {
    const userId = getCurrentUserId();
    if (!userId || !Array.isArray(messages) || !messages.length || !getCacheSwitch()) {
      return;
    }
    const grouped = new Map();
    messages.forEach((message) => {
      const conversationID = message?.conversationID;
      if (!conversationID) {
        return;
      }
      if (!grouped.has(conversationID)) {
        grouped.set(conversationID, []);
      }
      grouped.get(conversationID).push(message);
    });

    for (const [conversationID, list] of grouped.entries()) {
      const state = await readMessageState(userId, conversationID);
      list.forEach(item => {
        let findex = state.coreMessages.findIndex(e => e.ID == item.ID);
        if (findex > -1) {
          state.coreMessages.splice(findex, 1);
        }
      })
      trace('deleteMessage');
      await writeMessageState(userId, conversationID, state, DEFAULT_DEBOUNCE_MS);
    }
  }

  const getMessageList = async (options, otherOptions) => {
    inc('messageList', 'total');
    const chatClient = await getChatClient();
    const userId = getCurrentUserId();
    const conversationID = options?.conversationID;
    const requestCursor = options?.nextReqMessageID;
    let role = otherOptions?.role;
    if (!conversationID || !userId || !getCacheSwitch()) {
      inc('messageList', 'bypass');
      inc('messageList', 'remote');
      trace('MISS messageList -> SDK.getMessageList (reason: bypass cache)', { conversationID, userId, hasConversationID: !!conversationID });
      return chatClient.getMessageList(options);
    }

    const state = await readMessageState(userId, conversationID);

    let isContinuous = false;
    const conversationLastMessageFingerprint = await resolveConversationLastMessageFingerprint(userId, conversationID);
    if (!state.coreMessages.length) isContinuous = false;
    else {
      if (conversationLastMessageFingerprint.type == TencentCloudChat.TYPES.CONV_GROUP) {
        isContinuous = getMessageSeq(state.coreMessages[state.coreMessages.length - 1]) >= conversationLastMessageFingerprint.lastSeq;
      } else {
        isContinuous = getMessageTime(state.coreMessages[state.coreMessages.length - 1]) >= conversationLastMessageFingerprint.lastTime;
      }
    }

    if (!isContinuous && state.detachedMessages.length) {
      // 确保不会产生二次游离数据集
      let isDetachedContinuous = false;
      if (conversationLastMessageFingerprint.type == TencentCloudChat.TYPES.CONV_GROUP) {
        isDetachedContinuous = getMessageSeq(state.detachedMessages[state.detachedMessages.length - 1]) >= conversationLastMessageFingerprint.lastSeq;
      } else {
        isDetachedContinuous = getMessageTime(state.detachedMessages[state.detachedMessages.length - 1]) >= conversationLastMessageFingerprint.lastTime;
      }
      if (!isDetachedContinuous) {
        state.detachedMessages = [];
        state.detachedBoundaryNextReqMessageID = null;
        trace('游离缓存不连续，已清空', { conversationID, conversationLastMessageFingerprint, detachedLastMessage: state.detachedMessages[state.detachedMessages.length - 1] });
        inc('writes', 'detachedClear');
      }
    }


    // 不连续时，始终优先走 detached 通道；即便 detached 为空，也要触发一次远端最新页拉取来补洞。
    let targetKey = isContinuous ? 'coreMessages' : 'detachedMessages';

    let page = getPageBeforeCursor(state[targetKey] || [], requestCursor, MESSAGE_PAGE_SIZE, role, userId);
    do {
      const sourceMessages = state[targetKey] || [];
      let remoteTriggered = false;
      const needRemote = page.length < MESSAGE_PAGE_SIZE;
      if (needRemote && (!isContinuous || targetKey !== 'coreMessages' || !state.coreBoundaryCompleted)) {
        // 只有“连续 + 核心缓存 + 已明确到边界”时才允许跳过远端。
        // let fetchCursor = requestCursor;
        // if (!fetchCursor) {
        //   if (targetKey === 'detachedMessages') {
        //     fetchCursor = sourceMessages[0] ? getMessageId(sourceMessages[0]) : undefined;
        //   } else if (!state.coreBoundaryCompleted) {
        //     fetchCursor = state.coreBoundaryNextReqMessageID || undefined;
        //   }
        // }

        // if (!(isContinuous && targetKey === 'coreMessages' && state.coreBoundaryCompleted)) {
        remoteTriggered = true;
        inc('messageList', 'remote');
        trace('MISS messageList -> SDK.getMessageList (reason: page insufficient)', {
          conversationID,
          target: targetKey,
          isContinuous,
          cacheCount: sourceMessages.length,
          pageCount: page.length,
          requestCursor: requestCursor || '',
          // fetchCursor: fetchCursor || '',
        });
        var getTask = Promise.value;
        var existMsg = sourceMessages.length ? sourceMessages[0] : null;
        if (existMsg) {
          if (conversationLastMessageFingerprint.type == TencentCloudChat.TYPES.CONV_GROUP) {
            getTask = chatClient.getMessageListHopping({
              conversationID,
              sequence: getMessageSeq(existMsg) - 1,
              count: 15
            });
          } else {
            getTask = chatClient.getMessageListHopping({
              conversationID,
              time: getMessageTime(existMsg),
              count: 15
            });
          }
        } else {
          getTask = chatClient.getMessageList({
            conversationID,
            nextReqMessageID: null,
          });
        }
        const remoteRes = await getTask;
        const remoteList = mergeMessagesWithAssign([], remoteRes?.data?.messageList || []);

        if (remoteList.length) {
          if (targetKey === 'detachedMessages') {
            state.detachedMessages = mergeMessagesWithAssign(state.detachedMessages, remoteList);
            state.detachedBoundaryNextReqMessageID = getMessageId(state.detachedMessages[0]);
            state.detachedBoundaryCompleted = !!remoteRes?.data?.isCompleted;
            const coreIdSet = new Set(state.coreMessages.map((item) => getMessageId(item)));
            const hasIntersection = state.detachedMessages.some((item) => coreIdSet.has(getMessageId(item)));
            if (hasIntersection) {
              // 游离数据集与核心数据集产生交集，可合并
              state.coreMessages = mergeMessagesWithAssign(state.coreMessages, state.detachedMessages);
              state.detachedMessages = [];
              state.detachedBoundaryNextReqMessageID = null;
              if (state.coreMessages.length && getMessageId(state.coreMessages[0]) === remoteRes?.data?.nextReqMessageID) {
                // 本次获取的数据集已经触底了核心数据集的边界，更新核心边界状态
                state.coreBoundaryCompleted = remoteRes?.data?.isCompleted;
                state.coreBoundaryNextReqMessageID = state.coreMessages.length ? getMessageId(state.coreMessages[0]) : null;
              }
              targetKey = "coreMessages";
            } else if (state.detachedMessages.length >= state.coreMessages.length) {
              // 游离数据集长度超过核心数据集，且无交集，说明核心数据集过小了，可以直接替换核心数据集（相当于核心数据集过期了），并清空游离数据集
              state.coreMessages = state.detachedMessages;
              state.coreBoundaryNextReqMessageID = state.coreMessages.length ? getMessageId(state.coreMessages[0]) : null;
              state.detachedMessages = [];
              state.detachedBoundaryNextReqMessageID = null;
              state.coreBoundaryCompleted = !!remoteRes?.data?.isCompleted;
              targetKey = "coreMessages";
            }
          } else {
            state.coreMessages = mergeMessagesWithAssign(state.coreMessages, remoteList);
            state.coreBoundaryNextReqMessageID = state.coreMessages.length ? getMessageId(state.coreMessages[0]) : null;
            state.coreBoundaryCompleted = !!remoteRes?.data?.isCompleted;
          }

          await writeMessageState(userId, conversationID, state, DEFAULT_DEBOUNCE_MS);
        } else if (targetKey === 'coreMessages') {
          state.coreBoundaryNextReqMessageID = state.coreMessages.length ? getMessageId(state.coreMessages[0]) : null;
          state.coreBoundaryCompleted = !!remoteRes?.data?.isCompleted;
          await writeMessageState(userId, conversationID, state, DEFAULT_DEBOUNCE_MS);
        } else if (targetKey === 'detachedMessages') {
          state.detachedBoundaryCompleted = !!remoteRes?.data?.isCompleted;
        }
        // }
      }

      if (!needRemote) {
        inc('messageList', 'hit');
        trace('HIT messageList', {
          conversationID,
          target: targetKey,
          pageCount: page.length,
          requestCursor: requestCursor || '',
        });
      } else if (!remoteTriggered) {
        inc('messageList', 'hit');
        inc('messageList', 'boundaryHit');
        trace('HIT messageList (boundary completed, no remote)', {
          conversationID,
          target: targetKey,
          pageCount: page.length,
          requestCursor: requestCursor || '',
        });
      } else {
        inc('messageList', 'miss');
      }

      const finalSource = state[targetKey] || [];
      page = getPageBeforeCursor(finalSource, requestCursor, MESSAGE_PAGE_SIZE, role, userId);
      // 如果是主数据集，读取主数据集的完成状态。如果是游离数据集，读取游离结束状态
      // page = filterMessage(page, role); 筛选已内置到 getPageBeforeCursor
      console.log('getMessageList-page2', page);
    } while (page.length < MESSAGE_PAGE_SIZE && (targetKey == 'coreMessages' ? !state.coreBoundaryCompleted : !state.detachedBoundaryCompleted));//page.length < MESSAGE_PAGE_SIZE && (!state.coreBoundaryCompleted)


    return composeMessageListResponse({
      list: page,
      state,
      targetKey
    });
  };

  const filterMessage = (data, role) => {
    const userId = getCurrentUserId();
    return data.filter(e => isVisibleMsg(e, role, userId));
  }

  return {
    getConversationList,
    getMessageList,
    writeMsgsExt,
    readMsgsExt,
    setMsgExt,
    getMsgExt,
    handleConversationListUpdate,
    deleteMessage,
    handleMessageReadReceipt,
    userRevokeMessage,
    handleMessageReceived,
    onLoginSuccess,
    getDebugStats: () => safeClone(stats),
    printDebugStats: () => {
      const snapshot = safeClone(stats);
      console.log('[IM_CACHE_STATS]', snapshot);
      return snapshot;
    },
    resetDebugStats: () => {
      stats = createStats();
      return safeClone(stats);
    },
    setVerboseTrace: (enabled) => {
      verboseTrace = !!enabled;
    },
  };
};
